import { ImportCompartmentsDiff } from '@ekkogmbh/apisdk';
import { AllInbox, Warning } from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import { Box, Button, Stack, SvgIconProps, Tab, Tabs, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { ReactText, useCallback, useEffect, useMemo, useState } from 'react';
import { ContentPanel, StepDefinition, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { CSVDropzone } from 'src/Common/Components/CSVDropzone';
import { UploadStep } from 'src/Common/Components/CSVImportStepper/UploadStep';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import useTaskCollections from 'src/Common/Providers/TaskCollectionProvider';

interface Props {
  closeHandler: () => void;
}

interface ImportPanelTabDefinition {
  description: string;
  icon: React.ComponentType<SvgIconProps>;
  disableDiffModalOverride?: boolean;
}

const Component = ({ closeHandler }: Props): React.JSX.Element => {
  const { api, config } = useEkko();
  const { refresh } = usePagination();
  const { subscribe } = useTaskCollections();
  const { currentStep, enableNext, enableSave, setStep, stepNext } = usePanel();

  const [isLoading, startLoading] = useRequest();

  const [isDiffFailed, setDiffFailed] = useState<boolean>(false);
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [importDiff, setImportDiff] = useState<ImportCompartmentsDiff | undefined>();
  const [file, setFile] = useState<File>();

  const tabDefinitions: ImportPanelTabDefinition[] = [
    {
      description: 'Compartments',
      icon: AllInbox,
    },
    {
      description: 'Links',
      icon: LinkIcon,
      disableDiffModalOverride: true,
    },
  ];

  /** enabled panel buttons based on state */
  useEffect(() => {
    const hasFile = file !== undefined;
    enableSave(hasFile);
    enableNext(currentStep === 0 && hasFile);
  }, [currentStep, file]);

  /** load import diff when opening dialog */
  useEffect(() => {
    if (!isDialogOpen || file === undefined) {
      return;
    }

    startLoading(
      () => api.importCompartmentsDiff(file),
      (diff) => setImportDiff(diff as ImportCompartmentsDiff),
      (error) => {
        enqueueSnackbar(error.message);
        setImportDiff(undefined);
        setDiffFailed(true);
      },
    );
  }, [isDialogOpen, file]);

  const onReset = useCallback(() => {
    setFile(undefined);
    setStep(0);
  }, []);

  const doImport = useCallback(async () => {
    if (file === undefined) {
      return;
    }

    // compartments
    if (currentTab === 0) {
      startLoading(
        () => api.importCompartments(file, subscribe),
        () => {
          closeHandler();
          refresh();
        },
      );
    }

    // links
    if (currentTab === 1) {
      startLoading(
        () => api.importLinks(file, subscribe),
        () => {
          closeHandler();
          refresh();
        },
      );
    }
  }, [currentTab, file, closeHandler]);

  const onImport = useCallback(async () => {
    const { importDiffModal } = config;

    if (!!importDiffModal) {
      setDialogOpen(true);
    } else {
      await doImport();
    }
  }, [config.importDiffModal, setDialogOpen, doImport]);

  const tabs = useMemo(
    () => (
      <Tabs value={currentTab} onChange={(_, value: number) => setCurrentTab(value)}>
        {tabDefinitions.map((def, index) => {
          const { description } = def;

          return (
            <Tab
              key={index}
              label={
                <div
                  style={{
                    display: 'inline-flex',
                    justifyContent: 'center',
                    lineHeight: '42px',
                  }}
                >
                  <def.icon style={{ margin: 8 }} />
                  {description}
                </div>
              }
            />
          );
        })}
      </Tabs>
    ),
    [tabDefinitions, currentTab, setCurrentTab],
  );

  const diffDialog = useMemo(() => {
    if (!isDialogOpen) {
      return;
    }

    const importCount = importDiff?.count ?? 0;

    const compartmentsString = importCount === 1 ? 'Compartment' : 'Compartments';
    const zeroDiff = importCount === 0 ? 'not ' : undefined;
    const actualCount = importCount === 0 ? 'any' : importCount;

    const importDiffModal = config?.importDiffModal;
    const autoConfirmTime = !!importDiffModal ? Number(importDiffModal) : 0;

    const boldSpanFn = (text?: ReactText) => (
      <span
        style={{
          fontSize: 24,
          fontWeight: 700,
          verticalAlign: 'middle',
        }}
      >
        {text}
      </span>
    );

    const autoConfirmTextFn = (autoConfirmSecondsLeft: number) => (
      <>
        The import starts automatically in <span style={{ fontWeight: 700 }}>{autoConfirmSecondsLeft}</span> seconds.
      </>
    );
    return (
      <GenericDialog
        open
        fullWidth
        centered
        type={'confirmation'}
        maxWidth={'sm'}
        title={'Import Compartments'}
        text={
          <CenteredColumn>
            {isDiffFailed ? (
              <CenteredColumn>
                <Warning color={'warning'} />
                <Typography fontWeight={500}>Failed estimating import</Typography>
              </CenteredColumn>
            ) : (
              <Typography>
                This import will {boldSpanFn(zeroDiff)}insert/update {boldSpanFn(actualCount)} {compartmentsString}.
              </Typography>
            )}
          </CenteredColumn>
        }
        onClose={() => {
          setImportDiff(undefined);
          setDiffFailed(false);
          setDialogOpen(false);
        }}
        onConfirm={doImport}
        autoConfirm={autoConfirmTime > 0}
        autoConfirmTime={autoConfirmTime > 0 ? autoConfirmTime : undefined}
        autoConfirmTextFn={autoConfirmTime > 0 ? autoConfirmTextFn : undefined}
        buttonText={{ ok: 'import', cancel: 'close' }}
      />
    );
  }, [config.importDiffModal, isDialogOpen, importDiff, doImport, setDialogOpen, setImportDiff, isDiffFailed]);

  const fileStep: StepDefinition = useMemo(
    () => ({
      title: 'File-Selection',
      contentCallback: () => (
        <Stack direction={'column'} spacing={2} justifyContent={'flex-start'} alignItems={'center'}>
          {tabs}
          <Box width={'50%'}>
            <CSVDropzone
              onDrop={(f) => {
                setFile(f);
                if (f !== undefined) {
                  stepNext();
                }
              }}
            />
          </Box>
        </Stack>
      ),
    }),
    [tabs],
  );

  const uploadStep: StepDefinition = useMemo(
    () => ({
      title: 'Upload / Import',
      contentCallback: () => (
        <Stack spacing={2} direction={'column'}>
          <StyledFormHeader label={(currentTab === 0 ? 'compartment' : 'link') + ' import'} />
          <UploadStep file={file} loading={isLoading} />
          {currentTab === 0 && (
            <Button onClick={() => setDialogOpen(true)}>Calculate amount of changed compartments</Button>
          )}
        </Stack>
      ),
    }),
    [isLoading, file, currentTab, setDialogOpen],
  );

  return (
    <>
      {diffDialog}
      <ContentPanel
        steps={[fileStep, uploadStep]}
        initialStep={0}
        onCancel={closeHandler}
        onReset={onReset}
        onSave={onImport}
        buttonLabels={{
          save: 'Import',
        }}
      />
    </>
  );
};

export const ImportPanel = Component;
